import { useState, Fragment, useEffect, useMemo } from 'react'
import { Route, Redirect } from 'react-router-dom'
import { Header, Segment, Grid, Modal, Divider, List } from 'semantic-ui-react'
import { connect, useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import MenuHeader from 'components/Header/ClientHeader'
import ClientSidebarMenu from '../Sidebar/ClientSidebarMenu'
import clientActions from '../../store/actions/clients'
import Swal from 'sweetalert2'
import { initSocketHandlers } from 'utils/socket/chat/handlers'
import chat_passengerActions from '../../store/actions/chat_passenger'
import { useLateralMenu } from 'hooks/useLateralMenu'
import { useTabNavigation, ViewRegistrar } from 'hooks/useTabNavigation'

const PrivateClientRouteContent = ({
	Component,
	routeProps,
	sidebarOptions,
	isAuthenticated,
	isAllowed,
	permissionsReady,
	client,
	logout,
	rest,
}) => {
	const { registerActiveView, openTab } = useTabNavigation()
	const {
		setExpand,
		previousExpand,
		setPreviousExpand,
		mobileOnly,
		setMobileOnly,
	} = useLateralMenu()
	const [openModal, setOpenModal] = useState(false)
	const [sidebarMenuOptions, setSidebarMenuOptions] = useState(null)
	const handleSidebarMenuOpen = (options) => setSidebarMenuOptions(options)

	const memoizedRouteProps = useMemo(
		() => ({ ...routeProps, sidebarOptions: sidebarMenuOptions || sidebarOptions }),
		[routeProps, sidebarOptions, sidebarMenuOptions]
	)

	const memoizedLayoutConfig = useMemo(
		() => ({
			Header: MenuHeader,
			Sidebar: ClientSidebarMenu,
			sidebarWidth: 225,
			sidebarProps: {
				client,
				rest: rest,
				setExpand,
				previousExpand,
				setPreviousExpand,
				openModal,
				setOpenModal,
				mobileOnly,
				setMobileOnly,
				onMenuOpen: handleSidebarMenuOpen,
				onTabOpen: (route) => openTab(route, routeProps.history.push, false),
			},
			layoutProps: { logout: logout, rest: rest },
		}),
		[
			client,
			logout,
			rest,
			setExpand,
			previousExpand,
			setPreviousExpand,
			openModal,
			mobileOnly,
			setMobileOnly,
			handleSidebarMenuOpen,
			openTab,
			routeProps.history.push,
		]
	)

	if (!isAuthenticated) return <Redirect to='/login' />

	return (
		<>
			<ViewRegistrar
				registerActiveView={registerActiveView}
				Component={
					!permissionsReady ? EmptyComponent : isAllowed ? Component : ModuleNotFound
				}
				routeProps={memoizedRouteProps}
				layoutConfig={memoizedLayoutConfig}
			/>
			<Modal
				closeIcon
				basic
				centered={true}
				onClose={() => setOpenModal(false)}
				onOpen={() => setOpenModal(true)}
				open={openModal}
				size='large'
			>
				<Modal.Content>
					<Segment inverted style={{ height: '100%' }}>
						<Grid stackable divided inverted>
							<Grid.Row columns={2}>
								<Grid.Column width={6} verticalAlign='middle' textAlign='center'>
									<Header inverted content='© Geologistic GPS 2025' />
								</Grid.Column>
								<Grid.Column width={8}>
									<Header inverted content='Contacto' />
									<Divider></Divider>
									<Header
										size='small'
										inverted
										content='¿Necesitas registrar un nuevo ticket o solicitud?'
									/>
									<List divided inverted>
										<List.Item
											header='Ingresa al Portal: '
											content='https://geologistic.com.mx/Soporte/'
											style={{ cursor: 'pointer' }}
											onClick={() => {
												window.open(
													'https://geologistic.com.mx/Soporte/',
													'_blank'
												)
											}}
										/>
										<List.Item
											header='Soporte'
											content='(686) 214 5447, (686) 385 6938,  (686) 510 9915'
										/>
										<List.Item header='Oficina' content='(686) 372 9877' />
										<List.Item
											header='Emergencias'
											content='(686) 420 3333, (686) 214 5447, (686) 385 6938, (686) 510 9915,  (686) 123 6454'
										/>
										<List.Item header='Administración' content='(686) 122 0316' />
										<List.Item
											header='Ventas'
											content='(686) 510 9915, (686) 196 4736, (686) 420 8586,  (686) 191 1571'
										/>
									</List>
								</Grid.Column>
							</Grid.Row>
						</Grid>
					</Segment>
				</Modal.Content>
			</Modal>
		</>
	)
}

const INHERITED_ROUTES = {
	'/client/passengers': [
		'/client/client_group_passengers',
		'/client/client_shift_passengers',
		'/client/client_area_passengers',
        '/client/client_supervisor_passengers',
	],
}

const PrivateClientRoute = (props) => {
	const { isAuthenticated = true, component: Component, ...rest } = props
	const dispatch = useDispatch()
	const [allowedRoutes, setAllowedRoutes] = useState([])
	const [isLoading, setIsLoading] = useState(true)
	const permissionsReady = !isLoading

	useEffect(() => {
		if (!props.client?.id) return
		const loadPermissions = async () => {
			try {
				const clientAllowedRoutes = await props.getWebClientPermissions(props.client.id)
				setAllowedRoutes(clientAllowedRoutes || [])
			} catch (error) {
				setAllowedRoutes([])
			} finally {
				setIsLoading(false)
			}
		}
		loadPermissions()
	}, [props.client?.id, props.getWebClientPermissions])

	useEffect(() => {
		if (!props.client) return
		dispatch(initSocketHandlers())
	}, [dispatch, props.client])

	return (
		<Route
			{...rest}
			render={(routeProps) => {
			const isAllowed = allowedRoutes.some((allowedRoute) => {
					if (routeProps.location.pathname.startsWith(allowedRoute)) return true
					const inherited = INHERITED_ROUTES[allowedRoute] || []
					return inherited.some((route) => routeProps.location.pathname.startsWith(route))
				})
				return (
					<PrivateClientRouteContent
						Component={Component}
						routeProps={routeProps}
						isAuthenticated={isAuthenticated}
						isAllowed={isAllowed}
						permissionsReady={permissionsReady}
						client={props.client}
						logout={rest.logout}
						rest={rest}
					/>
				)
			}}
		/>
	)
}

const mapStateToProps = (state) => ({
	isAuthenticated: !!state.app.client,
	client: state.app.client,
	unreadMessages: state.chat_passengers?.unreadMessages,
})

const mapDispatchToProps = (dispatch) => ({
	getWebClientPermissions: (clientId) =>
		dispatch(clientActions.getWebClientPermissions(clientId)),
	clientLogout: (option) => dispatch(clientActions.clientLogout(option)),
	setActiveChatId: (id) => dispatch(chat_passengerActions.setActiveChatId(id)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivateClientRoute)

const EmptyComponent = () => null

const ModuleNotFound = () => {
	const { t } = useTranslation()
	return (
		<div style={{ padding: '50px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
			<Header as="h1" icon="warning circle" content={t("Módulo no encontrado")} />
			<p>{t("No tienes acceso a este módulo o no existe.")}</p>
		</div>
	)
}
